import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../services/api';
import { toast } from 'react-toastify';
import { PlusIcon, PencilIcon, ClockIcon, ChartBarIcon } from '@heroicons/react/outline';

const InsuranceTypes = () => {
  const { t } = useLanguage();
  const [insuranceTypes, setInsuranceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRateModal, setShowRateModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    isActive: true
  });

  const [rateFormData, setRateFormData] = useState({
    commissionType: 'FIXED',
    commissionValue: '',
    effectiveFrom: new Date().toISOString().split('T')[0]
  });

  const [commissionHistory, setCommissionHistory] = useState([]);

  useEffect(() => {
    fetchInsuranceTypes();
  }, [includeInactive]);

  const fetchInsuranceTypes = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/insurance-types', {
        params: { includeInactive }
      });
      setInsuranceTypes(response.data);
    } catch (error) {
      console.error('Error fetching insurance types:', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/admin/insurance-types', formData);
      toast.success(t('insuranceTypes.created'));
      setShowCreateModal(false);
      setFormData({ name: '', code: '', description: '', isActive: true });
      fetchInsuranceTypes();
    } catch (error) {
      console.error('Error creating insurance type:', error);
      toast.error(error.response?.data?.error || t('common.error'));
    }
  };

  const handleUpdate = async (id, updates) => {
    try {
      await api.put(`/api/admin/insurance-types/${id}`, updates);
      toast.success(t('insuranceTypes.updated'));
      fetchInsuranceTypes();
    } catch (error) {
      console.error('Error updating insurance type:', error);
      toast.error(error.response?.data?.error || t('common.error'));
    }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm(t('insuranceTypes.confirmDeactivate'))) return;
    
    try {
      await api.delete(`/api/admin/insurance-types/${id}`);
      toast.success(t('insuranceTypes.deactivated'));
      fetchInsuranceTypes();
    } catch (error) {
      console.error('Error deactivating insurance type:', error);
      toast.error(error.response?.data?.error || t('common.error'));
    }
  };

  const handleSetRate = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/api/admin/insurance-types/${selectedType.id}/commission-rates`, rateFormData);
      toast.success(t('insuranceTypes.rateSet'));
      setShowRateModal(false);
      setRateFormData({
        commissionType: 'FIXED',
        commissionValue: '',
        effectiveFrom: new Date().toISOString().split('T')[0]
      });
      fetchInsuranceTypes();
    } catch (error) {
      console.error('Error setting commission rate:', error);
      toast.error(error.response?.data?.error || t('common.error'));
    }
  };

  const viewHistory = async (type) => {
    try {
      const response = await api.get(`/api/admin/insurance-types/${type.id}/commission-history`);
      setCommissionHistory(response.data);
      setSelectedType(type);
      setShowHistoryModal(true);
    } catch (error) {
      console.error('Error fetching commission history:', error);
      toast.error(t('common.error'));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ar-LY', {
      style: 'currency',
      currency: 'LYD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('ar-LY', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">{t('insuranceTypes.title')}</h1>
          <p className="text-gray-600 mt-1">{t('insuranceTypes.subtitle')}</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <PlusIcon className="w-5 h-5" />
          {t('insuranceTypes.addNew')}
        </button>
      </div>

      {/* Filter Toggle */}
      <div className="mb-4">
        <label className="flex items-center gap-2 text-gray-700">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
            className="rounded text-blue-600"
          />
          {t('insuranceTypes.showInactive')}
        </label>
      </div>

      {/* Insurance Types Grid */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">{t('common.loading')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {insuranceTypes.map((type) => (
            <div
              key={type.id}
              className={`bg-white rounded-lg shadow-md p-6 ${
                !type.isActive ? 'opacity-60 border-2 border-gray-300' : 'border border-gray-200'
              }`}
            >
              {/* Header */}
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800">{type.name}</h3>
                  <p className="text-sm text-gray-500 font-mono">{type.code}</p>
                </div>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-semibold ${
                    type.isActive
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {type.isActive ? t('common.active') : t('common.inactive')}
                </span>
              </div>

              {/* Description */}
              {type.description && (
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">{type.description}</p>
              )}

              {/* Current Rate */}
              {type.commissionRates && type.commissionRates.length > 0 && (
                <div className="bg-blue-50 rounded-lg p-3 mb-4">
                  <p className="text-xs text-gray-600 mb-1">{t('insuranceTypes.currentRate')}</p>
                  <p className="text-lg font-bold text-blue-900">
                    {type.commissionRates[0].commissionType === 'FIXED'
                      ? formatCurrency(type.commissionRates[0].commissionValue)
                      : `${type.commissionRates[0].commissionValue}%`}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('insuranceTypes.since')} {formatDate(type.commissionRates[0].effectiveFrom)}
                  </p>
                </div>
              )}

              {/* Statistics */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-gray-50 rounded p-2">
                  <p className="text-xs text-gray-600">{t('insuranceTypes.totalDocs')}</p>
                  <p className="text-lg font-bold text-gray-800">
                    {type.statistics?.totalDocuments || 0}
                  </p>
                </div>
                <div className="bg-gray-50 rounded p-2">
                  <p className="text-xs text-gray-600">{t('insuranceTypes.totalCommission')}</p>
                  <p className="text-lg font-bold text-gray-800">
                    {formatCurrency(type.statistics?.totalCommission || 0)}
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setSelectedType(type);
                    setShowRateModal(true);
                  }}
                  className="flex-1 flex items-center justify-center gap-1 bg-green-600 text-white px-3 py-2 rounded text-sm hover:bg-green-700 transition-colors"
                >
                  <ChartBarIcon className="w-4 h-4" />
                  {t('insuranceTypes.setRate')}
                </button>
                <button
                  onClick={() => viewHistory(type)}
                  className="flex items-center justify-center gap-1 bg-gray-600 text-white px-3 py-2 rounded text-sm hover:bg-gray-700 transition-colors"
                >
                  <ClockIcon className="w-4 h-4" />
                  {t('insuranceTypes.history')}
                </button>
                {type.isActive && (
                  <button
                    onClick={() => handleDeactivate(type.id)}
                    className="bg-red-600 text-white px-3 py-2 rounded text-sm hover:bg-red-700 transition-colors"
                  >
                    {t('common.deactivate')}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              {t('insuranceTypes.createNew')}
            </h2>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('insuranceTypes.name')} *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('insuranceTypes.code')} *
                </label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono"
                  required
                  placeholder="e.g., CUSTOM_TYPE"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('insuranceTypes.description')}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows="3"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setFormData({ name: '', code: '', description: '', isActive: true });
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {t('common.create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Set Rate Modal */}
      {showRateModal && selectedType && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              {t('insuranceTypes.setCommissionRate')}
            </h2>
            <p className="text-gray-600 mb-4">{selectedType.name}</p>
            <form onSubmit={handleSetRate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('insuranceTypes.commissionType')} *
                </label>
                <select
                  value={rateFormData.commissionType}
                  onChange={(e) => setRateFormData({ ...rateFormData, commissionType: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="FIXED">{t('insuranceTypes.fixedAmount')}</option>
                  <option value="PERCENTAGE">{t('insuranceTypes.percentage')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('insuranceTypes.commissionValue')} *
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    value={rateFormData.commissionValue}
                    onChange={(e) => setRateFormData({ ...rateFormData, commissionValue: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                    min="0"
                  />
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                    {rateFormData.commissionType === 'PERCENTAGE' ? '%' : 'LYD'}
                  </span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('insuranceTypes.effectiveFrom')} *
                </label>
                <input
                  type="date"
                  value={rateFormData.effectiveFrom}
                  onChange={(e) => setRateFormData({ ...rateFormData, effectiveFrom: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  {t('insuranceTypes.rateNote')}
                </p>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowRateModal(false);
                    setRateFormData({
                      commissionType: 'FIXED',
                      commissionValue: '',
                      effectiveFrom: new Date().toISOString().split('T')[0]
                    });
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  {t('insuranceTypes.setRate')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* History Modal */}
      {showHistoryModal && selectedType && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[80vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              {t('insuranceTypes.commissionHistory')}
            </h2>
            <p className="text-gray-600 mb-4">{selectedType.name}</p>
            
            {commissionHistory.length === 0 ? (
              <p className="text-center text-gray-500 py-8">{t('insuranceTypes.noHistory')}</p>
            ) : (
              <div className="space-y-3">
                {commissionHistory.map((rate) => (
                  <div
                    key={rate.id}
                    className={`border rounded-lg p-4 ${
                      !rate.effectiveTo ? 'border-green-500 bg-green-50' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-semibold text-lg">
                          {rate.commissionType === 'FIXED'
                            ? formatCurrency(rate.commissionValue)
                            : `${rate.commissionValue}%`}
                        </p>
                        <p className="text-sm text-gray-600">
                          {rate.commissionType === 'FIXED'
                            ? t('insuranceTypes.fixedAmount')
                            : t('insuranceTypes.percentage')}
                        </p>
                      </div>
                      {!rate.effectiveTo && (
                        <span className="bg-green-600 text-white px-2 py-1 rounded text-xs font-semibold">
                          {t('insuranceTypes.current')}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600">
                      <p>
                        {t('insuranceTypes.from')}: {formatDate(rate.effectiveFrom)}
                      </p>
                      <p>
                        {t('insuranceTypes.to')}: {rate.effectiveTo ? formatDate(rate.effectiveTo) : t('insuranceTypes.present')}
                      </p>
                      {rate.creator && (
                        <p className="mt-2 text-xs">
                          {t('insuranceTypes.createdBy')}: {rate.creator.username}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <div className="mt-6">
              <button
                onClick={() => setShowHistoryModal(false)}
                className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InsuranceTypes;

